// The badge / PIN sign-in of a returns task screen (polishing, replenishing) on a P&P station — Fernando 28.10:
// "station + badge, every step recorded under the person". On a person's own login it shows nothing. The station
// session ends by itself when idle (pp_identity.STATION_IDLE_MIN); the next write then answers 401 badge_required and
// the screen asks again (`ask`). -- Claude, 2026-10-28
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch, API } from '../api';
import IdentifyBox, { Identity } from '../pp2/IdentifyBox';
import { returnsText } from '../returnsText';

export type DeskWho = { station: boolean; person: string | null; name?: string; lang?: string; kind?: string | null; can_work?: boolean };

/** `need`: the task the person signs in for (their floor right): 'returns_accept' | 'returns_scan' | 'replenish' |
 *  'inbound'. `whoUrl`: the screen's own "who is working here" endpoint (the inbound stations have theirs, 29.10). */
/** `eager` (default true): the box opens on load when nobody is signed in at the station — the returns desk. The
 *  inbound screens pass false (Fernando 31.10: anyone may LOOK at an inbound; the badge is asked when work starts,
 *  i.e. when a write answers 401 badge_required and the screen calls askAgain). */
export function useDeskSignIn(need: string, whoUrl = `${API}/returns/desk/who`, { eager = true }: { eager?: boolean } = {}) {
  const [who, setWho] = useState<DeskWho | null>(null);
  const [ask, setAsk] = useState(false);
  const after = useRef<(() => void) | null>(null);          // the refused action, run once the sign-in succeeded
  const load = useCallback(async () => {
    const r = await authFetch(whoUrl);
    if (!r.ok) return;
    const d: DeskWho = await r.json();
    setWho(d);
    if (eager) setAsk(d.station && !d.person);
  }, [whoUrl, eager]);
  useEffect(() => { load(); }, [load]);
  const t = returnsText(who?.lang);
  const signIn = async (id: Identity): Promise<string | null> => {
    const r = await authFetch(`${API}/pp2/station/sign-in`, { method: 'POST', body: JSON.stringify({ ...id, need }) });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      return typeof d.detail === 'string' ? d.detail : d.detail?.message || 'Not accepted';
    }
    await load();
    setAsk(false);
    const f = after.current; after.current = null;
    if (f) setTimeout(f, 0);                                 // the action that was refused runs now, no second press
    return null;
  };
  const signOut = async () => { await authFetch(`${API}/pp2/session/end`, { method: 'POST' }); await load(); };
  /** after a write answered 401 badge_required: ask again; `retry` = that write, run once the sign-in succeeded */
  const askAgain = (retry?: () => void) => { after.current = retry || null; setAsk(true); };
  const box = ask ? <IdentifyBox title={t.whoTitle} hint={t.badgeOrPin} cancelLabel={t.cancelBadge} onSubmit={signIn} onCancel={() => { after.current = null; setAsk(false); }} /> : null;
  const chip = who?.person ? (
    <span className="text-sm text-gray-400">
      {t.working(who.name || who.person)}
      {who.station && <button onClick={signOut} className="ml-3 underline hover:text-white">{t.signOut}</button>}
    </span>
  ) : null;
  return { who, box, chip, askAgain, asking: ask };
}

/** A 401 that means "nobody is signed in at this station" — not an expired login. */
export async function isBadgeRequired(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;
  const d = await res.clone().json().catch(() => ({}));
  return d?.detail?.code === 'badge_required';
}
